import { ref } from 'vue';
function reorderArray(arr, fromIndex, toIndex) {
    const result = [...arr];
    const [removed] = result.splice(fromIndex, 1);
    result.splice(toIndex, 0, removed);
    return result;
}
export default function useRowDrag(rowDrag, data, _getRowKey, prefixCls, emit) {
    const draggingIndex = ref(null);
    const droppedIndex = ref(null);
    const rowDragging = ref(false);
    const isEnabled = () => {
        const val = rowDrag.value;
        if (!val)
            return false;
        if (typeof val === 'boolean')
            return val;
        return val.enabled;
    };
    const canDragRow = (record, index) => {
        const val = rowDrag.value;
        if (!val)
            return false;
        if (typeof val === 'boolean')
            return true;
        if (val.canDrag)
            return val.canDrag(record, index);
        return true;
    };
    const hasHandle = () => {
        const val = rowDrag.value;
        if (!val || typeof val === 'boolean')
            return false;
        return !!val.handleColumnKey;
    };
    /**
     * Build the customRow wrapper that adds drag events to each row.
     * This merges with the user's existing customRow if present.
     */
    const buildDragCustomRow = (userCustomRow) => {
        return (record, index) => {
            const userProps = userCustomRow ? userCustomRow(record, index) : {};
            if (!isEnabled() || !canDragRow(record, index)) {
                return userProps;
            }
            const useHandle = hasHandle();
            const cls = prefixCls.value;
            return Object.assign(Object.assign({}, userProps), { draggable: !useHandle, class: [
                    userProps.class,
                    `${cls}-row-draggable`,
                    rowDragging.value && draggingIndex.value === index ? `${cls}-row-dragging` : '',
                ]
                    .filter(Boolean)
                    .join(' '), onMousedown: (event) => {
                    var _a;
                    (_a = userProps.onMousedown) === null || _a === void 0 ? void 0 : _a.call(userProps, event);
                    if (useHandle) {
                        // Check if the target or its parent has the drag handle attribute
                        const target = event.target;
                        const isHandle = target.closest(`[data-row-drag-handle]`) !== null;
                        if (isHandle) {
                            event.currentTarget.draggable = true;
                        }
                        else {
                            event.currentTarget.draggable = false;
                        }
                    }
                }, onDragstart: (event) => {
                    var _a, _b;
                    (_a = userProps.onDragstart) === null || _a === void 0 ? void 0 : _a.call(userProps, event);
                    rowDragging.value = true;
                    draggingIndex.value = index;
                    // Firefox requires this to make dragging possible
                    (_b = event.dataTransfer) === null || _b === void 0 ? void 0 : _b.setData('text', 'b');
                }, onDragover: (event) => {
                    var _a;
                    (_a = userProps.onDragover) === null || _a === void 0 ? void 0 : _a.call(userProps, event);
                    if (!rowDragging.value || draggingIndex.value === index)
                        return;
                    event.preventDefault();
                    const rowElement = event.currentTarget;
                    const rowY = rowElement.getBoundingClientRect().top;
                    const rowHeight = rowElement.offsetHeight;
                    const pageY = event.clientY;
                    const rowMidY = rowY + rowHeight / 2;
                    const prevRowElement = rowElement.previousElementSibling;
                    // Clean up previous indicators on this element and its neighbors
                    rowElement.classList.remove(`${cls}-row-dragpoint-top`, `${cls}-row-dragpoint-bottom`);
                    if (prevRowElement) {
                        prevRowElement.classList.remove(`${cls}-row-dragpoint-bottom`);
                    }
                    if (pageY < rowMidY) {
                        // Drop before this row
                        droppedIndex.value = index;
                        if (prevRowElement && prevRowElement.classList.contains(`${cls}-row`)) {
                            prevRowElement.classList.add(`${cls}-row-dragpoint-bottom`);
                        }
                        else {
                            rowElement.classList.add(`${cls}-row-dragpoint-top`);
                        }
                    }
                    else {
                        // Drop after this row
                        droppedIndex.value = index + 1;
                        rowElement.classList.add(`${cls}-row-dragpoint-bottom`);
                    }
                }, onDragleave: (event) => {
                    var _a;
                    (_a = userProps.onDragleave) === null || _a === void 0 ? void 0 : _a.call(userProps, event);
                    const rowElement = event.currentTarget;
                    const prevRowElement = rowElement.previousElementSibling;
                    rowElement.classList.remove(`${cls}-row-dragpoint-top`, `${cls}-row-dragpoint-bottom`);
                    if (prevRowElement) {
                        prevRowElement.classList.remove(`${cls}-row-dragpoint-bottom`);
                    }
                }, onDragend: (event) => {
                    var _a;
                    (_a = userProps.onDragend) === null || _a === void 0 ? void 0 : _a.call(userProps, event);
                    rowDragging.value = false;
                    draggingIndex.value = null;
                    droppedIndex.value = null;
                    event.currentTarget.draggable = !useHandle ? true : false;
                    // Clean up all drag indicators
                    const tableEl = event.currentTarget.closest(`.${cls}-wrapper`);
                    if (tableEl) {
                        tableEl
                            .querySelectorAll(`.${cls}-row-dragpoint-top, .${cls}-row-dragpoint-bottom`)
                            .forEach(el => {
                            el.classList.remove(`${cls}-row-dragpoint-top`, `${cls}-row-dragpoint-bottom`);
                        });
                    }
                }, onDrop: (event) => {
                    var _a;
                    (_a = userProps.onDrop) === null || _a === void 0 ? void 0 : _a.call(userProps, event);
                    event.preventDefault();
                    if (droppedIndex.value != null && draggingIndex.value != null) {
                        const fromIndex = draggingIndex.value;
                        let toIndex = fromIndex > droppedIndex.value
                            ? droppedIndex.value
                            : droppedIndex.value === 0
                                ? 0
                                : droppedIndex.value - 1;
                        if (fromIndex !== toIndex) {
                            const reorderedData = reorderArray(data.value, fromIndex, toIndex);
                            emit('rowDragEnd', {
                                originalEvent: event,
                                dragIndex: fromIndex,
                                dropIndex: toIndex,
                                data: reorderedData,
                            });
                        }
                    }
                    // Cleanup
                    const cls2 = prefixCls.value;
                    const rowElement = event.currentTarget;
                    const prevRowElement = rowElement.previousElementSibling;
                    rowElement.classList.remove(`${cls2}-row-dragpoint-top`, `${cls2}-row-dragpoint-bottom`);
                    if (prevRowElement) {
                        prevRowElement.classList.remove(`${cls2}-row-dragpoint-bottom`);
                    }
                    rowDragging.value = false;
                    draggingIndex.value = null;
                    droppedIndex.value = null;
                } });
        };
    };
    return {
        buildDragCustomRow,
        rowDragging,
        draggingIndex,
        droppedIndex,
    };
}
